/*
 * Drives the vertical thumbnail rail on the product page.
 *
 * It builds its own arrows rather than reusing the ones slider-component
 * ships. Dawn's slider is horizontal throughout: onButtonClick only ever sets
 * scrollLeft, and update() decides each button's disabled state from
 * isSlideVisible(), which compares offsetLeft against the slider's width. In a
 * single-column rail every thumbnail is horizontally visible, so Dawn marks
 * both buttons disabled — and it re-runs that on the scroll events this rail
 * fires, so the state cannot simply be set once and left. Its buttons stay in
 * the DOM, hidden, still owned by slider-component; these ones are ours.
 *
 * The arrows only appear when the rail actually overflows. Dawn hides its
 * buttons on media count instead, which is the right test for a row of
 * thumbnails under the image and the wrong one for a column beside it, where
 * what decides it is the image's height.
 */
if (!window.thumbnailRailLoaded) {
  window.thumbnailRailLoaded = true;

  class ThumbnailRail {
    constructor(gallery) {
      this.gallery = gallery;
      this.slider = gallery.querySelector('.thumbnail-slider');
      this.list = gallery.querySelector('.thumbnail-list');
      if (!this.slider || !this.list || this.slider.dataset.railReady) return;
      this.slider.dataset.railReady = 'true';

      this.mediaQuery = window.matchMedia('(min-width: 750px)');
      this.up = this.buildArrow('up');
      this.down = this.buildArrow('down');
      this.slider.insertBefore(this.up, this.list);
      this.slider.insertBefore(this.down, this.list.nextSibling);

      this.update = this.update.bind(this);
      this.list.addEventListener('scroll', this.update);
      this.mediaQuery.addEventListener('change', this.update);

      // The rail's height comes from the featured image, which is usually
      // still loading when this runs.
      if ('ResizeObserver' in window) {
        new ResizeObserver(this.update).observe(this.list);
      } else {
        window.addEventListener('resize', this.update);
      }

      this.update();
    }

    buildArrow(direction) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `thumbnail-rail__arrow thumbnail-rail__arrow--${direction}`;
      button.setAttribute('aria-controls', this.list.id);
      button.setAttribute('aria-label', direction === 'up' ? 'Previous thumbnails' : 'Next thumbnails');

      // Reuses the caret Dawn already rendered into its own buttons, so the
      // rail never carries a second copy of the icon.
      const icon = this.slider.querySelector('.slider-button .svg-wrapper');
      const wrapper = document.createElement('span');
      wrapper.className = 'svg-wrapper';
      if (icon) wrapper.innerHTML = icon.innerHTML;
      button.appendChild(wrapper);

      button.addEventListener('click', () => this.scrollByPage(direction === 'up' ? -1 : 1));
      return button;
    }

    scrollByPage(direction) {
      this.list.scrollBy({ top: this.list.clientHeight * direction, behavior: 'smooth' });
    }

    update() {
      const overflows = this.mediaQuery.matches && this.list.scrollHeight - this.list.clientHeight > 1;
      this.gallery.classList.toggle('thumbnail-rail--scrollable', overflows);
      if (!overflows) return;

      const top = this.list.scrollTop;
      const max = this.list.scrollHeight - this.list.clientHeight;
      this.up.toggleAttribute('disabled', top <= 1);
      this.down.toggleAttribute('disabled', top >= max - 1);
    }
  }

  const start = () => {
    document
      .querySelectorAll('media-gallery[data-desktop-layout^="thumbnail"]')
      .forEach((gallery) => new ThumbnailRail(gallery));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // The theme editor swaps the whole section out on every settings change.
  document.addEventListener('shopify:section:load', start);
}
